import { ampFetch } from './api';

/**
 * @typedef VehicleResponse
 * @property {string} account_id
 * @property {number} id
 * @property {string} [license_plate_number]
 * @property {string} [license_plate_state]
 * @property {string} [vin]
 * @property {string} [transfer_date] datetime
 * @property {boolean} is_active
 * @property {string} created_date datetime
 * @property {string} modified_date datetime
 * @property {string} [created_by_user_id]
 * @property {string} [modified_by_user_id]
 * @property {string} [year]
 * @property {string} [make]
 * @property {string} [model]
 * @property {string} [color]
 * @property {string} [body]
 * @property {number} vif_id
 * @property {number} color_id
 */

/**
 * @typedef VehicleSpecResponse
 * @property {string} [year]
 * @property {string} [make]
 * @property {string} [model]
 * @property {string} [color]
 * @property {string} [body]
 * @property {number} vif_id
 * @property {number} color_id
 */
/**
 * @typedef {'ChargePerUser'|'ChargePerVehicle'|'ChargePerVehicleSingle'|'ChargePerWash'} SubscriptionBillingApproach
 */
/**
 * @typedef AccountResponse
 * @property {string} name
 * @property {string} id
 * @property {AccountType} name
 * @property {SubscriptionBillingApproach} subscription_billing_approach
 * @property {string} user_id_primary_account_holder
 * @property {string} created_date datetime
 * @property {string} modified_date datetime
 * @property {string} [created_by_user_id]
 * @property {string} [modified_by_user_id]
 */

const garages = {
    /**
     * @param {string} account_name The garage name.
     * @returns {Promise<AccountResponse>}
     *
     * @throws error
     */
    createGarage: async (account_name) => {
        return await ampFetch({
            route: 'api-user/create-account',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    account_name
                })
            }
        });
    },
    /**
     * @typedef GetAccountsByUserResponse
     * @property {Array.<AccountResponse>} accounts
     */
    /**
     * @returns {Promise<GetAccountsByUserResponse>}
     */
    getGarages: async () => {
        return await ampFetch({
            route: 'api-user/get-accounts-by-user',
            options: {
                method: 'GET'
            }
        });
    },
    /**
     * @param {string} account_name The garage name.
     * @param {string} account_id The garage ID.
     * @returns {Promise<AccountResponse>}
     *
     * @throws error
     */
    updateGarageName: async (account_name, account_id) => {
        return await ampFetch({
            route: 'api-user/update-account',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'account_name': account_name,
                    'Amp-Account-Id': account_id
                })
            }
        });
    },

    /**
     * @param {string} account_id The garage ID.
     * @param {number} vif_id
     * @param {number} color_id
     * @param {string} license_plate_number
     * @param {string} license_plate_state
     * @param {string} [vin]
     * @param {string} [model_lab_override]
     * @returns {Promise<VehicleResponse>}
     */
    createVehicle: async (
        account_id,
        vif_id,
        color_id,
        license_plate_number,
        license_plate_state,
        vin,
        model_lab_override
    ) => {
        return await ampFetch({
            route: 'api-user/create-vehicle',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    vif_id,
                    color_id,
                    license_plate_number,
                    license_plate_state,
                    vin,
                    model_lab_override,
                    'Amp-Account-Id': account_id
                })
            }
        });
    },
    /**
     * @param {string} account_id The garage ID.
     * @param {number} year
     * @param {string} make
     * @param {string} model
     * @param {string} body
     * @param {string} color_name
     * @returns {Promise<VehicleSpecResponse>}
     */
    createCustomVehicle: async (
        account_id,
        year,
        make,
        model,
        body,
        color_name
    ) => {
        return await ampFetch({
            route: 'api-user/create-custom-vehicle',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    year,
                    make,
                    model,
                    body,
                    color_name,
                    'Amp-Account-Id': account_id
                })
            }
        });
    },
    /**
     * @param {string} account_id
     * @param {number} vehicle_id
     * @returns {Promise<VehicleResponse>}
     */
    removeVehicle: async (account_id, vehicle_id) => {
        return await ampFetch({
            route: 'api-user/remove-vehicle',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'vehicle_id': parseInt(vehicle_id, 10),
                    'Amp-Account-Id': account_id
                })
            }
        });
    },
    /**
     * @typedef GetVehicleDataByAccountResponse
     * @property {VehicleDataResponse} vehicleData
     */
    /**
     * @typedef VehicleDataResponse
     * @property {VehicleResponse} vehicle
     * @property {SubscriptionResponse} subscription
     */
    /**
     * @param {string} account_id
     * @returns {Promise<GetVehicleDataByAccountResponse>}
     */
    getVehiclesByAccount: async (account_id) => {
        return await ampFetch({
            route: 'api-user/get-vehicle-data-by-account',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'Amp-Account-Id': account_id
                })
            }
        });
    },
    getUsersByAccount: async (account_id) => {
        return await ampFetch({
            route: 'api-user/get-users-by-account',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'Amp-Account-Id': account_id
                })
            }
        });
    },
    addUserToAccount: async (account_id, email) => {
        return await ampFetch({
            route: 'api-user/add-user-to-account',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'Amp-Account-Id': account_id,
                    'email': email
                })
            }
        });
    },
    removeUserFromAccount: async (account_id, user_id) => {
        return await ampFetch({
            route: 'api-user/remove-user-from-account',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'Amp-Account-Id': account_id,
                    'user_id': user_id
                })
            }
        });
    },
    /**
     * Get all vehicles for a given user from all garages.
     *
     * @returns {Promise<void>}
     */
    getAllVehicles: async () => {}
};

export default garages;
